import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAnalytics } from '../context/AnalyticsContext'
import { useAuth } from '../context/AuthContext'
import '../pages/onboarding/onboarding.css'

/**
 * Ввод пароля для входа (отдельный флоу от онбординга).
 * Email приходит из location.state. Назад → /login.
 */
const LoginPassword = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { trackEvent } = useAnalytics()
  const { user, loading, signIn, resetPassword } = useAuth()
  const email = location.state?.email || ''
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loadingSignIn, setLoadingSignIn] = useState(false)
  const [forgotSent, setForgotSent] = useState(false)
  const [forgotLoading, setForgotLoading] = useState(false)

  useEffect(() => {
    if (user) {
      navigate('/app', { replace: true })
      return
    }
    if (!loading && !email) {
      navigate('/login', { replace: true })
    }
  }, [loading, user, email, navigate])

  const onBack = () => navigate('/login', { replace: true })

  const onContinue = async () => {
    if (!password || password.trim().length < 6) {
      setError(t('loginPassword.passwordMinLength'))
      return
    }
    if (!email) {
      setError(t('loginPassword.emailRequired'))
      return
    }
    setError('')
    setLoadingSignIn(true)
    try {
      const { user: u, error: signInError } = await signIn(email, password)
      if (signInError) {
        const msg = signInError.toLowerCase().includes('invalid') || signInError.toLowerCase().includes('incorrect')
          ? t('loginPassword.incorrectPassword')
          : signInError
        setError(msg)
        setLoadingSignIn(false)
        return
      }
      if (u) {
        trackEvent('login_completed')
        window.location.href = '/app'
      }
    } catch (err) {
      setError(err?.message || t('loginPassword.errorSigningIn'))
    } finally {
      setLoadingSignIn(false)
    }
  }

  const onForgot = async () => {
    if (!email) {
      setError(t('loginPassword.emailRequired'))
      return
    }
    setError('')
    setForgotLoading(true)
    try {
      const { error: resetError } = await resetPassword(email)
      if (resetError) {
        setError(resetError || t('onboarding.forgotPasswordError'))
        setForgotLoading(false)
        return
      }
      setForgotSent(true)
    } catch (err) {
      setError(t('onboarding.forgotPasswordError'))
    } finally {
      setForgotLoading(false)
    }
  }

  if (user) return null
  if (!email) return null

  if (forgotSent) {
    return (
      <div className="onboarding-page">
        <div className="onboarding-content" style={{ paddingTop: 48 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <button type="button" className="link" onClick={() => setForgotSent(false)} style={{ fontSize: 16 }}>
              ← {t('common.back')}
            </button>
          </div>
          <h1 className="h1">{t('loginPassword.checkEmail')}</h1>
          <p className="p">
            {t('onboarding.forgotPasswordSent', { email })}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="onboarding-page">
      <div className="onboarding-content" style={{ paddingTop: 48, paddingBottom: 120 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <button type="button" className="link" onClick={onBack} style={{ fontSize: 16 }}>
              ← {t('common.back')}
            </button>
        </div>
        <h1 className="h1">{t('loginPassword.enterPassword')}</h1>
        <p className="p">
          {t('loginPassword.subtitle')}
        </p>
        <div className="field">
          <label className="label">{t('loginPassword.passwordLabel')}</label>
          <input
            className={`input ${error ? 'error' : ''}`}
            type="password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError('') }}
            placeholder="••••••••"
          />
          {error ? <div className="error-text">{error}</div> : null}
        </div>
        <button type="button" className="link" onClick={onForgot} style={{ marginTop: 10 }} disabled={forgotLoading}>
          {t('loginPassword.forgotPassword')}
        </button>
      </div>
      <button className="cta-fixed" onClick={onContinue} disabled={loadingSignIn}>
        {loadingSignIn ? t('loginPassword.signingIn') : t('common.continue')}
      </button>
    </div>
  )
}

export default LoginPassword
